import { useCallback, useEffect, useState } from 'react'
import { ApiError, api, can, refusalReason, tierLabel, type Questionnaire, type RiskModel } from '../../lib/api'
import {
  TIER_ORDINALS,
  baselines,
  canMove,
  daysWord,
  moveRule,
  orderRules,
  placeRule,
  ruleTableRows,
  rulesAsWrites,
  type BaselineRulesView,
  type RuleWrite,
  type VendorGroup,
} from '../../lib/baselines'
import { ErrorBox, Loading } from '../../components/DataTable'
import { Status } from '../../components/kit'
import { ReasonTip } from '../../components/vendor/ReasonTip'
import '../../styles/baselines.css'

// Settings, Baselines: who gets which questionnaire (release 3.4, M7).
//
// # WHY THIS SCREEN EXISTS
//
// A tier default sent every vendor of a tier the same questionnaire, and the
// mentor's point 6 is that a programme does not work that way: the payment
// processors get the payment assessment, the cloud services on the top tiers
// the cloud one. So this is one rule table, read in the order the server reads
// it (packages/core/domain/baselines.go): every group rule, then every type
// rule, then the tier defaults, which are shown here and changed where they
// always were, under Reassessment. Each row says how many vendors it decides
// now, so a rule that covers nobody is visible as one.
//
// Every change saves the whole list at once, against the version it was read
// at; somebody else's save in between is refused, and the screen says so
// rather than overwriting it.

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

const BLANK: RuleWrite = { kind: 'GROUP', tiers: [], questionnaireId: '', answerDays: 21, enabled: true }

export function BaselinesSettings() {
  const [view, setView] = useState<BaselineRulesView | null>(null)
  const [groups, setGroups] = useState<VendorGroup[]>([])
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([])
  const [models, setModels] = useState<RiskModel[]>([])
  const [error, setError] = useState<unknown>(null)
  const [saveError, setSaveError] = useState<ApiError | null>(null)
  const [saving, setSaving] = useState(false)
  // The rule being written: its place in the list (null for a new one).
  const [editing, setEditing] = useState<{ at: number | null; rule: RuleWrite } | null>(null)
  const mayWrite = can('config.write')

  const load = useCallback(() => {
    baselines
      .rules()
      .then((v) => {
        setView(v)
        setError(null)
      })
      .catch(setError)
  }, [])

  useEffect(load, [load])
  useEffect(() => {
    baselines
      .groups()
      .then((g) => setGroups(g.groups))
      .catch(() => setGroups([]))
    Promise.all([api.questionnaires(), api.riskModels()])
      .then(([q, m]) => {
        setQuestionnaires(q.questionnaires.filter((x) => x.publishedVersionId && !x.isArchived))
        setModels(m.riskModels.filter((x) => x.activeVersionId))
      })
      .catch(() => undefined)
  }, [])

  function save(next: RuleWrite[]) {
    if (!view) return
    setSaving(true)
    setSaveError(null)
    baselines
      .saveRules(view.version, next)
      .then((v) => {
        setView(v)
        setEditing(null)
      })
      .catch((err) => setSaveError(asApiError(err)))
      .finally(() => setSaving(false))
  }

  if (error) return <ErrorBox error={error} what="The baseline rules" />
  if (!view) return <Loading what="the baseline rules" />

  const writes = rulesAsWrites(view.rules)
  const stale = saveError?.code === 'RULES_CHANGED'

  return (
    <section className="panel bl-panel">
      <div className="bl-head">
        <div>
          <h2 className="sw-row">
            Baselines{' '}
            <ReasonTip
              label="How a baseline is chosen"
              text="A group rule is read first, then a type rule, each in the order shown. A vendor no rule covers gets its tier default. A rule that is switched off, or whose questionnaire is archived or unpublished, is passed over."
            />
          </h2>
          <p className="muted">Which questionnaire each vendor is sent, and how long it has to answer.</p>
        </div>
        {mayWrite ? (
          <button type="button" className="primary" onClick={() => setEditing({ at: null, rule: { ...BLANK } })} disabled={editing !== null}>
            Add rule
          </button>
        ) : (
          <span className="sw-row">
            <Status tone="neutral">Read only</Status>
            <ReasonTip label="Why you cannot change the rules" text={refusalReason('config.write')} />
          </span>
        )}
      </div>

      {saveError && (
        <div className="note warn" role="alert">
          {stale ? (
            <>
              <strong>The rules changed since you opened them</strong>
              <p>Somebody else saved them. Your change was not saved.</p>
              <button type="button" onClick={() => (setSaveError(null), setEditing(null), load())}>
                Read them again
              </button>
            </>
          ) : (
            <ErrorBox error={saveError} what="The baseline rules" />
          )}
        </div>
      )}

      {editing && (
        <RuleEditor
          rule={editing.rule}
          isNew={editing.at === null}
          groups={groups}
          vendorTypes={view.vendorTypes}
          questionnaires={questionnaires}
          models={models}
          saving={saving}
          onCancel={() => setEditing(null)}
          onSave={(rule) => save(placeRule(writes, rule, editing.at))}
        />
      )}

      <RuleTable
        view={view}
        mayWrite={mayWrite}
        busy={saving || editing !== null}
        onEdit={(at) => setEditing({ at, rule: writes[at]! })}
        onSave={save}
      />
      <p className="muted bl-foot">
        A vendor with no baseline is given one on the bulk send. Groups are kept under <a href="#/settings?t=vendor-groups">Vendor groups</a>.
      </p>
    </section>
  )
}

// The rule table of the addendum: the rules in the order they are read, the
// tier defaults after them, and the vendors nothing covers. Every change is a
// whole new list handed to onSave.
export function RuleTable({
  view,
  mayWrite,
  busy,
  onEdit,
  onSave,
}: {
  view: BaselineRulesView
  mayWrite: boolean
  busy: boolean
  onEdit: (at: number) => void
  onSave: (next: RuleWrite[]) => void
}) {
  const writes = rulesAsWrites(view.rules)
  const ordered = orderRules(view.rules)
  const rows = ruleTableRows(view)
  const save = onSave
  return (
    <div className="table-wrap">
      <table className="grid bl-rules">
        <caption className="vh">Baseline rules in the order they are read, then the tier defaults, with how many vendors each decides.</caption>
        <thead>
          <tr>
            <th scope="col">Order</th>
            <th scope="col">When</th>
            <th scope="col">Baseline</th>
            <th scope="col">Due in</th>
            <th scope="col" className="num">
              Vendors
            </th>
            {mayWrite && (
              <th scope="col">
                <span className="vh">Change</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const i = ordered.findIndex((r) => r.id === row.key)
            const rule = i >= 0 ? ordered[i] : undefined
            return (
              <tr key={row.key} className={row.problem ? 'bl-passed' : row.kind === 'TIER' || row.kind === 'NONE' ? 'bl-default' : undefined}>
                <td className="mono">{row.order}</td>
                <td>
                  {row.when}
                  {row.problem && <span className="cell-sub">{row.problem}</span>}
                </td>
                <td>{row.baseline}</td>
                <td>{row.dueIn}</td>
                <td className="num mono">{row.vendors}</td>
                {mayWrite && (
                  <td className="bl-actions">
                    {rule && (
                      <>
                        <button type="button" className="linkish" onClick={() => onEdit(i)} disabled={busy}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="linkish"
                          aria-label={'Move up: ' + row.when}
                          onClick={() => save(moveRule(writes, i, -1))}
                          disabled={busy || !canMove(writes, i, -1)}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          className="linkish"
                          aria-label={'Move down: ' + row.when}
                          onClick={() => save(moveRule(writes, i, 1))}
                          disabled={busy || !canMove(writes, i, 1)}
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          className="linkish"
                          onClick={() => save(writes.map((w, j) => (j === i ? { ...w, enabled: !w.enabled } : w)))}
                          disabled={busy}
                        >
                          {rule.enabled ? 'Turn off' : 'Turn on'}
                        </button>
                        <button type="button" className="linkish" onClick={() => save(writes.filter((_, j) => j !== i))} disabled={busy}>
                          Remove
                        </button>
                      </>
                    )}
                    {row.kind === 'TIER' && <a href="#/settings?t=reassessment">Change</a>}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function RuleEditor({
  rule,
  isNew,
  groups,
  vendorTypes,
  questionnaires,
  models,
  saving,
  onCancel,
  onSave,
}: {
  rule: RuleWrite
  isNew: boolean
  groups: VendorGroup[]
  vendorTypes: { code: string; label: string }[]
  questionnaires: Questionnaire[]
  models: RiskModel[]
  saving: boolean
  onCancel: () => void
  onSave: (r: RuleWrite) => void
}) {
  const [draft, setDraft] = useState<RuleWrite>(rule)
  const set = (patch: Partial<RuleWrite>) => setDraft((d) => ({ ...d, ...patch }))
  const days = Number(draft.answerDays)
  const ready =
    (draft.kind === 'GROUP' ? Boolean(draft.groupId) : Boolean(draft.vendorType)) &&
    Boolean(draft.questionnaireId) &&
    Number.isInteger(days) &&
    days >= 1 &&
    days <= 90

  function toggleTier(t: number) {
    set({ tiers: draft.tiers.includes(t) ? draft.tiers.filter((x) => x !== t) : [...draft.tiers, t] })
  }

  return (
    <form
      className="fieldset bl-editor"
      aria-label={isNew ? 'New rule' : 'Edit rule'}
      onSubmit={(e) => {
        e.preventDefault()
        if (!ready) return
        const out: RuleWrite = { ...draft, answerDays: days }
        if (out.kind === 'GROUP') {
          delete out.vendorType
          out.tiers = []
        } else {
          delete out.groupId
        }
        if (!out.riskModelId) delete out.riskModelId
        onSave(out)
      }}
    >
      <fieldset className="bl-kind">
        <legend>The rule is about</legend>
        <label className="radio-inline">
          <input type="radio" name="bl-kind" checked={draft.kind === 'GROUP'} onChange={() => set({ kind: 'GROUP' })} />
          A vendor group
        </label>
        <label className="radio-inline">
          <input type="radio" name="bl-kind" checked={draft.kind === 'TYPE'} onChange={() => set({ kind: 'TYPE' })} />
          A vendor type
        </label>
      </fieldset>

      <div className="bl-editor-grid">
        {draft.kind === 'GROUP' ? (
          <div className="field">
            <label htmlFor="bl-group">Group</label>
            <select id="bl-group" value={draft.groupId ?? ''} onChange={(e) => set({ groupId: e.target.value })}>
              <option value="">Choose a group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.members})
                </option>
              ))}
            </select>
            {groups.length === 0 && (
              <p className="field-note">
                No group yet. Add one under <a href="#/settings?t=vendor-groups">Vendor groups</a>.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="field">
              <label htmlFor="bl-type">Vendor type</label>
              <select id="bl-type" value={draft.vendorType ?? ''} onChange={(e) => set({ vendorType: e.target.value })}>
                <option value="">Choose a type</option>
                {vendorTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <fieldset className="field bl-tiers">
              <legend>Tiers (none chosen is every tier)</legend>
              <div className="chiprow">
                {TIER_ORDINALS.map((t) => (
                  <button key={t} type="button" className="chip-toggle" aria-pressed={draft.tiers.includes(t)} onClick={() => toggleTier(t)}>
                    {tierLabel(t)}
                  </button>
                ))}
              </div>
            </fieldset>
          </>
        )}
        <div className="field">
          <label htmlFor="bl-questionnaire">Questionnaire</label>
          <select id="bl-questionnaire" value={draft.questionnaireId} onChange={(e) => set({ questionnaireId: e.target.value })}>
            <option value="">Choose a questionnaire</option>
            {questionnaires.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="bl-model">Risk model</label>
          <select id="bl-model" value={draft.riskModelId ?? ''} onChange={(e) => set({ riskModelId: e.target.value })}>
            <option value="">The tier default</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.code}
              </option>
            ))}
          </select>
        </div>
        <div className="field bl-days">
          <label htmlFor="bl-days">Answer within (days)</label>
          <input
            id="bl-days"
            type="number"
            min={1}
            max={90}
            value={String(draft.answerDays)}
            aria-describedby="bl-days-note"
            onChange={(e) => set({ answerDays: e.target.value === '' ? 0 : Number(e.target.value) })}
          />
          <p className="field-note" id="bl-days-note">
            1 to 90. {Number.isInteger(days) && days >= 1 && days <= 90 ? daysWord(days) + ' from the day it is sent.' : ''}
          </p>
        </div>
        <label className="bl-on">
          <input type="checkbox" checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Rule is on
        </label>
      </div>
      <div className="sw-row">
        <button type="submit" className="primary" disabled={!ready || saving}>
          {saving ? 'Saving' : isNew ? 'Add rule' : 'Save rule'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  )
}
